/**
 * `--safe-bottom`, put right where the browser reports a bar the page is not under.
 *
 * Firefox on Android reports the system navigation bar as `safe-area-inset-bottom`
 * — 47px of three buttons on the test Fold — while ending the page above that bar,
 * not behind it. The tab bar then pads itself for buttons it never meets, and a
 * blank strip as tall as the bar opens under «Заказ» and «Профиль».
 *
 * The inset is only meant for content drawn under the bar, so this asks where the
 * page actually ends on the screen. If the stretch between that and the bottom of
 * the screen already covers the inset, the bar lies outside the page and the inset
 * comes off. Only Firefox says where its page sits (`mozInnerScreenY`); every
 * other browser keeps the stylesheet's `env()` untouched, as does Firefox the day
 * it draws edge to edge and the measurement comes out flush.
 *
 * Once found out, it stays off for the life of the tab. Firefox slides the inset
 * down to nothing and back with its toolbar and fires no event when it returns, so
 * a decision taken afresh on each resize gets caught at zero and is wrong again a
 * scroll later. Until there is an inset to judge — the toolbar may be away at
 * start — it keeps looking.
 */
const pageTop = (): number | undefined => (window as { mozInnerScreenY?: number }).mozInnerScreenY;

export function startInsets(): void {
  if (typeof pageTop() !== 'number') return;

  const check = (): void => {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:fixed;width:0;height:env(safe-area-inset-bottom,0px);visibility:hidden';
    document.body.append(probe);
    const inset = probe.getBoundingClientRect().height;
    probe.remove();
    if (inset <= 0) return;

    const below = screen.height - (pageTop() ?? 0) - innerHeight;
    // A pixel's slack: the three numbers are rounded separately.
    if (below >= inset - 1) document.documentElement.style.setProperty('--safe-bottom', '0px');
    // Either way that was the answer: an inset the page reaches is a real one.
    window.removeEventListener('resize', check);
    window.visualViewport?.removeEventListener('resize', check);
  };

  // A turn of the phone, or Firefox's toolbar sliding, moves where the page ends;
  // the visual viewport hears the slide frame by frame, the window only at its end.
  window.addEventListener('resize', check);
  window.visualViewport?.addEventListener('resize', check);
  check();
}
